import React, { useState } from 'react';

import { Select } from '@aline/common';
import { Typography } from '@mui/material';
import type { AccountOption, AccountsAutocompleteProps } from 'src/components/AccountsAutocomplete';
import { AccountsAutocomplete } from 'src/components/AccountsAutocomplete';
import { AlineTextarea } from 'src/components/AlineTextarea';
import type { AlineDialogProps } from 'src/components/generic';
import {
  AlineButton,
  AlineDialog,
  AlineTabs,
  AlineTooltip,
  Spacer,
  TabPanel,
} from 'src/components/generic';
import { PersonIcon } from 'src/components/icons/PersonIcon';
import type { RoleOption, RolesAutoCompleteProps } from 'src/components/RolesAutocomplete';
import { RolesAutoComplete } from 'src/components/RolesAutocomplete';
import { WhatsThis } from 'src/components/WhatsThis';
import { useCollabDialogStyles } from 'src/components/workflow/workflow-collaborators/ChangeCollaboratorRoleDialog';
import type { ExistingCollaboratorOption } from 'src/components/workflow/workflow-collaborators/WorkflowCollaboratorsAutocomplete';
import { Apollo } from 'src/gql/generated';
import { doNothing, tooltipMsg } from 'src/helpers';
import {
  useAddWorkflowPermissions,
  useGetOrgMembers,
  useGetRoleFromOption,
  useGetWorkflowPermissions,
} from 'src/hooks';

enum CollaboratorTab {
  Placeholder = 'Placeholder',
  User = 'User',
}

interface AddCollaboratorForm {
  accounts: AccountOption[];
  role: RoleOption | null;
  note?: string;
}

export enum AddCollaboratorDialogVariant {
  NoTabs = 'NoTabs',
  Tabs = 'Tabs',
}

interface AddCollaboratorDialogProps {
  handleSubmit: (form: AddCollaboratorForm) => void;
  onClose: () => void;
  open: boolean;
  disabledReason?: string;
  filterAccountsPred?: AccountsAutocompleteProps['filterPred'];
  getIsAccountOptionDisabled?: AccountsAutocompleteProps['getIsOptionDisabled'];
  getIsRoleOptionDisabled?: RolesAutoCompleteProps['getIsOptionDisabled'];
  initialValues?: AddCollaboratorForm;
  maxAccountOptions?: number;
  showMessage?: boolean;
  TransitionProps?: AlineDialogProps['TransitionProps'];
  variant?: AddCollaboratorDialogVariant;
}

export const defaultAddCollaboratorForm: AddCollaboratorForm = {
  accounts: [],
  role: null,
};

export function AddCollaboratorDialog({
  TransitionProps,
  disabledReason = '',
  filterAccountsPred,
  getIsAccountOptionDisabled = () => false,
  getIsRoleOptionDisabled = () => false,
  handleSubmit,
  initialValues = defaultAddCollaboratorForm,
  maxAccountOptions,
  onClose,
  open,
  showMessage = true,
  variant = AddCollaboratorDialogVariant.Tabs,
}: AddCollaboratorDialogProps): JSX.Element {
  const { classes } = useCollabDialogStyles();
  const { org, orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [form, setForm] = useState<AddCollaboratorForm>(initialValues);
  const [tab, setTab] = useState<CollaboratorTab>(CollaboratorTab.User);
  const noFormValue = !form.accounts.length && !form.role;

  function resetState(): void {
    setTab(CollaboratorTab.User);
    setForm(defaultAddCollaboratorForm);
    setIsSubmitting(false);
  }

  return (
    <AlineDialog
      TransitionProps={{
        ...TransitionProps,
        onExit: node => {
          resetState();
          TransitionProps?.onExit?.(node);
        },
      }}
      classes={{ paper: classes.paper }}
      open={open}
      title={
        <div className={classes.title}>
          <PersonIcon />
          <Typography variant="h1">Add Collaborator</Typography>
        </div>
      }
      onClose={onClose}
    >
      {variant === AddCollaboratorDialogVariant.Tabs && (
        <div className={classes.nav}>
          <AlineTabs<CollaboratorTab>
            tabs={[
              {
                label: 'User',
                value: CollaboratorTab.User,
              },
              {
                label: 'Placeholder',
                value: CollaboratorTab.Placeholder,
              },
            ]}
            value={tab}
            onChange={value => setTab(value)}
          />
          <div className={classes.rightNav} />
        </div>
      )}
      <TabPanel tabValue={CollaboratorTab.User} value={tab}>
        <div className={classes.userContainer}>
          <AccountsAutocomplete
            accounts={org ? [org, ...orgMembers] : orgMembers}
            dataCy={Select.components.addCollaboratorsDialog.accountsAutocomplete}
            filterPred={filterAccountsPred}
            getIsOptionDisabled={getIsAccountOptionDisabled}
            maxOptions={maxAccountOptions}
            value={form.accounts}
            onChange={(_, accounts) => setForm(form => ({ ...form, accounts, role: null }))}
          />
          {showMessage && (
            <AlineTextarea
              maxRows={10}
              minRows={5}
              placeholder="Add a message for new collaborators..."
              value={form.note}
              onChange={e => setForm(form => ({ ...form, note: e.target.value }))}
            />
          )}
        </div>
      </TabPanel>
      <TabPanel tabValue={CollaboratorTab.Placeholder} value={tab}>
        <RolesAutoComplete
          filterSelectedOptions
          getIsOptionDisabled={getIsRoleOptionDisabled}
          value={form.role}
          onChange={(_, role) => setForm(form => ({ ...form, accounts: [], role }))}
        />
      </TabPanel>
      <div className={classes.formSubmitButtons}>
        {tab === CollaboratorTab.Placeholder && (
          <WhatsThis hintText="What's a placeholder?" tooltipMsg={tooltipMsg.placeholder} />
        )}
        <Spacer />
        <AlineButton color="primary" disabled={isSubmitting} variant="outlined" onClick={onClose}>
          Cancel
        </AlineButton>
        <AlineTooltip arrow placement="top" title={disabledReason}>
          <div>
            <AlineButton
              color="primary"
              data-cy={Select.components.addCollaboratorsDialog.submitButton}
              disabled={Boolean(disabledReason) || noFormValue}
              loading={isSubmitting}
              variant="contained"
              onClick={async () => {
                setIsSubmitting(true);
                await handleSubmit(form);
                onClose();
              }}
            >
              {tab === CollaboratorTab.User ? 'Add Collaborator' : 'Add Placeholder'}
            </AlineButton>
          </div>
        </AlineTooltip>
      </div>
    </AlineDialog>
  );
}

interface AddWorkflowCollaboratorDialogProps extends Omit<
  AddCollaboratorDialogProps,
  'handleSubmit'
> {
  templateTimelinessCheck: Apollo.WorkflowPermissionTimeliness;
  workflow: Pick<Apollo.Workflow, 'id' | 'variant' | '__typename'>;
  afterSubmit?: (permissions: ExistingCollaboratorOption[]) => void;
  permissionProps?: Partial<Apollo.NewWorkflowPermissionProps>;
}

export function AddWorkflowCollaboratorDialog(props: AddWorkflowCollaboratorDialogProps) {
  const {
    afterSubmit = doNothing,
    open,
    permissionProps,
    templateTimelinessCheck,
    workflow,
  } = props;
  const { getRoleFromOption } = useGetRoleFromOption();
  const { addWorkflowPermissions } = useAddWorkflowPermissions();
  const { permissions, refetch } = useGetWorkflowPermissions(workflow.id, { skip: !open });
  const isContractWorkflow = workflow?.variant === Apollo.WorkflowVariant.Contract;

  return (
    <AddCollaboratorDialog
      TransitionProps={{
        onEnter: async () => {
          await refetch();
        },
      }}
      getIsAccountOptionDisabled={account => {
        if (account.__typename === 'NewUser') return false;

        const conflictingPermissionExists = permissions.some(permission => {
          const isConflictingAccountRolePair =
            permission.account?.id === account.id && !permission.role;
          if (isContractWorkflow) return isConflictingAccountRolePair;

          return isConflictingAccountRolePair && permission.timeliness === templateTimelinessCheck;
        });
        return conflictingPermissionExists;
      }}
      getIsRoleOptionDisabled={role => {
        if (role.__typename === 'NewRole') return false;

        const roleAlreadyUsedInWF = permissions.some(p => p?.role?.id === role.id);
        return roleAlreadyUsedInWF;
      }}
      handleSubmit={async form => {
        const { assignedRoleId } = await getRoleFromOption(form.role);
        const props: Apollo.NewWorkflowPermissionProps[] = form.accounts.length
          ? form.accounts.map(
              (account): Apollo.NewWorkflowPermissionProps => ({
                email: account.__typename === 'NewUser' ? account.email : null,
                orgId: account.__typename === 'Org' ? account.id : null,
                userId: account.__typename === 'User' ? account.id : null,
                ...permissionProps,
              })
            )
          : form.role
            ? [
                {
                  roleId: assignedRoleId,
                  ...permissionProps,
                } satisfies Apollo.NewWorkflowPermissionProps,
              ]
            : [];

        if (props.length) {
          const { data } = await addWorkflowPermissions(workflow.id, {
            variables: { input: { message: form.note, props, workflowId: workflow.id } },
          });
          await afterSubmit(data?.addWorkflowPermissions ?? []);
        }
      }}
      showMessage={isContractWorkflow}
      {...props}
    />
  );
}
